import { useCallback, useMemo, useState } from 'react';
import { Avatar, Button } from '@aimsos/ui';
import { useTranslation } from '@aimsos/i18n';
import { Plus, Users } from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';

import Can from '../components/guards/Can';
import AssignItemModal from '../components/member-tabs/AssignItemModal';
import MemberGroupCard from '../components/member-tabs/MemberGroupCard';
import MemberTabEmpty from '../components/member-tabs/MemberTabEmpty';
import MemberTabGuard from '../components/member-tabs/MemberTabGuard';
import { MEMBER_STATUS } from '../constants/copy';
import { GROUP_PERMISSIONS } from '../constants/groupPermissions';
import { useMemberGroups } from '../hooks/useMemberGroups';
import { getAvatarColorClass } from '../utils/avatarColor';
import { groupAvatarText } from '../utils/groupPaths';

/**
 * Groups tab of the member detail: the groups they are in (open the group, remove with Undo) and
 * Assign group. Groups organize people and grant no access.
 *
 * @param {object} props
 * @param {object} props.member UI member (see `mapApiMemberToUi`)
 * @param {string} props.basePath Where the host mounted People & Access (links to the group page).
 */
function MemberGroupsTab({ member, basePath }) {
  const { t } = useTranslation('people-access');
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { groups, assignable, isLoading, error, assign, remove, reload } = useMemberGroups(member);
  const [isAssigning, setIsAssigning] = useState(false);

  const handleOpenAssign = useCallback(() => setIsAssigning(true), []);
  const handleCloseAssign = useCallback(() => setIsAssigning(false), []);
  const handleOpenGroup = useCallback(
    (groupId) => navigate(`${basePath}/groups/${groupId}`, { state: { backTo: pathname } }),
    [navigate, basePath, pathname],
  );

  const items = useMemo(
    () =>
      assignable.map((group) => ({
        id: group.id,
        name: group.name,
        detail: t('memberTabs.groups.members', { count: group.memberCount }),
      })),
    [assignable, t],
  );
  const renderLeading = useCallback(
    (item) => (
      <Avatar
        size="md"
        initials={groupAvatarText(item.name)}
        alt={item.name}
        className={`${getAvatarColorClass(item.name)} text-[10px]`}
      />
    ),
    [],
  );

  return (
    <MemberTabGuard
      tabId="groups"
      icon={Users}
      isRemoved={member.status === MEMBER_STATUS.REMOVED}
      isLoading={isLoading}
      error={error}
      onRetry={reload}
    >
      <div className="flex flex-col gap-4">
        <div className="flex items-center justify-between gap-3">
          <span className="text-sm text-content-secondary">
            {groups.length > 0 ? t('memberTabs.groups.countAssigned', { count: groups.length }) : ''}
          </span>
          <Can permission={GROUP_PERMISSIONS.MANAGE}>
            <Button variant="secondary" size="sm" leftIcon={Plus} onClick={handleOpenAssign}>
              {t('memberTabs.groups.assign')}
            </Button>
          </Can>
        </div>
        {groups.length === 0 ? (
          <MemberTabEmpty
            icon={Users}
            title={t('memberTabs.groups.emptyTitle')}
            subtitle={t('memberTabs.groups.emptySubtitle')}
          />
        ) : (
          <div className="flex flex-col gap-2">
            {groups.map((group) => (
              <MemberGroupCard key={group.id} group={group} onOpen={handleOpenGroup} onRemove={remove} />
            ))}
          </div>
        )}
      </div>
      <AssignItemModal
        isOpen={isAssigning}
        title={t('memberTabs.groups.assignModal.title')}
        subtitle={t('memberTabs.groups.assignModal.subtitle', { name: member.displayName })}
        searchPlaceholder={t('memberTabs.groups.assignModal.searchPlaceholder')}
        emptyMessage={t('memberTabs.groups.assignModal.empty')}
        noMatchMessage={t('memberTabs.groups.assignModal.noMatch')}
        items={items}
        renderLeading={renderLeading}
        onClose={handleCloseAssign}
        onPick={assign}
      />
    </MemberTabGuard>
  );
}

export default MemberGroupsTab;
